// Which buttons a Call Logs row offers. Pure (no React / MUI) so it is testable
// without the components barrel.
//
// When the row carries server-computed `nextActions` (caller-types backend,
// CU-869fm5hhm) those decide; actions this page doesn't know yet are skipped.
// Otherwise the fallback rules below apply.
import { FOLLOW_UP_STATE } from './callLog.constants';
import { lineNotSetUp } from './caller/callerModel';

export const ROW_ACTION = {
  CALL_BACK: 'call_back',
  CREATE_LEAD: 'create_lead',
  FOLLOW_UP: 'follow_up',
  LOG_OUTCOME: 'log_outcome',
  REVIEW_CALL: 'review_call',
  OPEN_PATIENT: 'open_patient',
  LINK_EXISTING: 'link_existing',
  // Caller journeys (B4 v1.2 next_actions).
  PICK_LEAD: 'pick_lead',
  UNARCHIVE_AND_LINK: 'unarchive_and_link',
  LINK_KEEP_ARCHIVED: 'link_keep_archived',
  NOT_THEM: 'not_them',
  NOT_A_PATIENT: 'not_a_patient',
  // "No answer - try again later" on an unknown caller's row: one call-back attempt (B4 outcome no_answer).
  NO_ANSWER: 'no_answer',
  UNDO_NOT_A_PATIENT: 'undo_not_a_patient',
  // Client-side, for a call on a line that isn't set up (B3 recording_reason).
  SET_UP_LINE: 'set_up_line',
  ASK_ADMIN: 'ask_admin',
};
// Actions that open the caller-journey dialog (caller/CallerActionDialog.jsx).
export const CALLER_JOURNEY_ACTIONS = ['pick_lead', 'unarchive_and_link', 'link_keep_archived', 'not_them', 'not_a_patient', 'undo_not_a_patient'];
// What the call-back queue offers; anything else is hidden there. REVIEW_CALL is
// in the list because the queue is the tab staff work in, and calling someone
// back without first hearing the voicemail they left was the gap. `rowActions`
// only surfaces it when that caller's call actually has something to review.
export const QUEUE_ACTIONS = ['call_back', 'log_outcome', 'no_answer', 'create_lead', 'link_existing', 'open_patient', 'review_call', ...CALLER_JOURNEY_ACTIONS];
const DEFAULT_LABELS = {
  call_back: 'Call back',
  create_lead: 'Create lead',
  follow_up: 'Follow up',
  log_outcome: 'Log outcome',
  review_call: 'Review call',
  open_patient: 'Open patient',
  link_existing: 'Link to existing',
  pick_lead: 'Pick who it was',
  unarchive_and_link: 'Restore patient and link call',
  link_keep_archived: 'Link call, keep patient archived',
  not_them: 'Not them',
  not_a_patient: 'Not a patient',
  no_answer: 'No answer – try again later',
  undo_not_a_patient: 'Undo',
  set_up_line: 'Set up line',
  ask_admin: 'Ask an admin',
};
// Our wording wins over the server's label for these (it still sends "Unarchive & link").
const CLIENT_WORDING = ['unarchive_and_link', 'link_keep_archived'];
// Backend aliases for the same button.
const ALIASES = { find_contact: 'link_existing', link_to_existing: 'link_existing' };
// `disabled_reason` codes (B4 contract) → the hover text on a disabled button.
const DISABLED_REASONS = {
  needs_permission: "You don't have permission for this. Ask an admin.",
  withheld: 'The number was withheld.',
  undo_expired: 'The 30-day undo window has passed.',
  no_candidate: 'No single matching patient was found.',
};

// Outcomes where nobody spoke to the caller.
export const FOLLOW_UP_OUTCOMES = ['missed', 'busy', 'failed', 'voicemail'];

// A number staff can actually dial (not "Unknown" / withheld).
export const dialableNumber = (row) => {
  const value = typeof row?.contact === 'string' ? row.contact.trim() : '';
  return (value.match(/\d/g) || []).length >= 6 ? value : '';
};

// The server's next action of this id, if the row carries next_actions.
export const serverAction = (row, id) => (Array.isArray(row?.nextActions)
  ? row.nextActions.find((entry) => entry && typeof entry === 'object' && (entry.id || entry.key) === id) || null
  : null);

// Number for Call back: the server's (B4 call_back.number) when present, else the row's.
export const callBackNumber = (row) => {
  const fromServer = serverAction(row, 'call_back')?.number;
  return dialableNumber({ contact: typeof fromServer === 'string' && fromServer ? fromServer : row?.contact });
};

// The one dialer widget (V2-3, CU-869fmbgfj) opens on this window event; the widget
// cancels it when it opens. Same name as OPEN_DIALER_EVENT in V2-3's
// components/outbound/dialerModel.js, kept here so this page doesn't import the
// widget's files. TODO(CU-869fmbgfj): import OPEN_DIALER_EVENT once V2-3 is on staging.
export const OPEN_DIALER_EVENT = 'dialer:open';
// true when the dialer widget took the request.
export const requestDialer = (detail) => {
  if (typeof window === 'undefined') return false;
  return !window.dispatchEvent(new CustomEvent(OPEN_DIALER_EVENT, { cancelable: true, detail }));
};

// Which dialer Call back uses, never two at once: the one dialer widget when it is
// there (it picks desk or Twilio itself); until then the desk phone (Elevate
// click-to-call) when this user can call from a desk, else the Twilio widget, else
// the number is shown to dial on a handset.
export const CALL_BACK_DIALER = { WIDGET: 'widget', DESK: 'desk', TWILIO: 'twilio', HANDSET: 'handset', NONE: 'none' };
export function callBackDialer({ number, deskCanCall = false, twilioCanUse = false } = {}) {
  if (!number) return CALL_BACK_DIALER.NONE;
  if (deskCanCall) return CALL_BACK_DIALER.DESK;
  if (twilioCanUse) return CALL_BACK_DIALER.TWILIO;
  return CALL_BACK_DIALER.HANDSET;
}

// Outcome keys the server allows on this row (B4 log_outcome.options, e.g. withheld
// rows have no not_a_patient); null = no restriction known.
export const outcomeOptionsFor = (row) => {
  const options = serverAction(row, 'log_outcome')?.options;
  return Array.isArray(options) ? options : null;
};

// Why outcomes can't be logged on this row (server log_outcome disabled), else ''.
export const outcomeBlockedReason = (row) => {
  const entry = serverAction(row, 'log_outcome');
  if (!entry || (entry.enabled !== false && entry.disabled !== true)) return '';
  return DISABLED_REASONS[entry.disabled_reason] || "You can't log an outcome for this call.";
};

export const hasReviewContent = (row) => Boolean(row?.recordings?.length || row?.transcript || row?.aiReview || row?.hasTranscript);

const fromServer = (entry) => {
  const raw = typeof entry === 'string' ? { key: entry } : (entry || {});
  const rawKey = raw.id || raw.key || raw.action || raw.type;
  const key = ALIASES[rawKey] || rawKey;
  if (!DEFAULT_LABELS[key]) return null;
  return {
    key,
    label: typeof raw.label === 'string' && raw.label.trim() && !CLIENT_WORDING.includes(key) ? raw.label.trim() : DEFAULT_LABELS[key],
    primary: raw.primary === true,
    disabled: raw.enabled === false || raw.disabled === true,
    hint: DISABLED_REASONS[raw.disabled_reason] || raw.disabled_reason || '',
  };
};

const action = (key, extra = {}) => ({ key, label: DEFAULT_LABELS[key], primary: false, disabled: false, hint: '', ...extra });

// `available` = the action keys the page can handle (a missing handler hides the button).
function baseRowActions(row, available) {
  const can = (key) => available.includes(key);
  if (Array.isArray(row?.nextActions)) {
    const actions = row.nextActions.map(fromServer).filter((a) => a && can(a.key));
    // An unknown caller has no "Log outcome" and no question: the choices are right in the menu
    // (Create lead, No answer; Not a patient is the server's own action where it allows it).
    const logAt = row.lead?.id ? -1 : actions.findIndex((a) => a.key === ROW_ACTION.LOG_OUTCOME);
    if (logAt >= 0) {
      const log = actions[logAt];
      const known = outcomeOptionsFor(row);
      const swap = [];
      if (can(ROW_ACTION.CREATE_LEAD) && !actions.some((a) => a.key === ROW_ACTION.CREATE_LEAD)) swap.push(action(ROW_ACTION.CREATE_LEAD));
      if (can(ROW_ACTION.NO_ANSWER) && (!known || known.includes('no_answer'))) swap.push(action(ROW_ACTION.NO_ANSWER, { disabled: log.disabled, hint: log.hint }));
      actions.splice(logAt, 1, ...swap);
    }
    // The server list has no follow_up; a linked call keeps the lead follow-up popup
    // (call timer, booking, next step) as the last button.
    if (row.lead?.id && can(ROW_ACTION.FOLLOW_UP) && !actions.some((a) => a.key === ROW_ACTION.FOLLOW_UP)) {
      actions.push(action(ROW_ACTION.FOLLOW_UP));
    }
    return actions;
  }
  // Fallback: today's rules, with Call back / Create lead / Follow up split.
  if (hasReviewContent(row)) return can(ROW_ACTION.REVIEW_CALL) ? [action(ROW_ACTION.REVIEW_CALL, { primary: true })] : [];
  if (!FOLLOW_UP_OUTCOMES.includes(row?.outcome)) return [];
  const linked = Boolean(row?.lead?.id);
  const openCallBack = Boolean(FOLLOW_UP_STATE[row?.followUpState]?.open);
  const actions = [];
  if (openCallBack && dialableNumber(row) && can(ROW_ACTION.CALL_BACK)) {
    actions.push(action(ROW_ACTION.CALL_BACK, { primary: true }));
  }
  if (linked && can(ROW_ACTION.FOLLOW_UP)) actions.push(action(ROW_ACTION.FOLLOW_UP, { primary: !actions.length }));
  if (!linked && can(ROW_ACTION.CREATE_LEAD)) actions.push(action(ROW_ACTION.CREATE_LEAD, { primary: !actions.length }));
  if (!linked && openCallBack && can(ROW_ACTION.NO_ANSWER)) actions.push(action(ROW_ACTION.NO_ANSWER));
  return actions;
}

// A call on a line that isn't set up: admins get Set up line, everyone else Ask an admin.
// When the server's next_actions already carry one of them, that one is the button.
export function rowActions(row, available) {
  const actions = baseRowActions(row, available);
  if (!lineNotSetUp(row)) return actions;
  if (actions.some((a) => a.key === ROW_ACTION.SET_UP_LINE || a.key === ROW_ACTION.ASK_ADMIN)) return actions;
  if (available.includes(ROW_ACTION.SET_UP_LINE)) return [...actions, action(ROW_ACTION.SET_UP_LINE)];
  if (available.includes(ROW_ACTION.ASK_ADMIN)) return [...actions, action(ROW_ACTION.ASK_ADMIN)];
  return actions;
}

// One row's buttons for All calls: the main button, and the "⋯" menu (the rest of the
// row's actions plus the quick ones every row can use: Open patient for a linked call,
// Create lead / Link to existing for an unlinked one, Open call). A server-driven row
// (next_actions) only gets Create lead / Link to existing when the server offers them.
// A call with something to review: a recording, transcript or AI summary.
export const reviewable = (row) => Boolean(hasReviewContent(row) || row?.hasRecording || row?.aiReviewAvailable);

export function splitRowActions(row, available) {
  const actions = rowActions(row, available);
  const can = (key) => available.includes(key);
  const mainIndex = actions.findIndex((a) => a.primary);
  let main = actions.length ? actions[Math.max(0, mainIndex)] : null;
  // Review call is the main button whenever there is something to review; Open patient /
  // Create lead move behind "⋯". A call-back still owed stays the main button.
  if (reviewable(row) && can(ROW_ACTION.REVIEW_CALL) && main?.key !== ROW_ACTION.CALL_BACK) {
    main = actions.find((a) => a.key === ROW_ACTION.REVIEW_CALL) || action(ROW_ACTION.REVIEW_CALL, { primary: true });
  }
  const items = actions.filter((a) => a !== main && a.key !== main?.key);
  const shown = (key) => main?.key === key || actions.some((a) => a.key === key);
  const linked = Boolean(row?.lead?.id);
  const fallback = !Array.isArray(row?.nextActions);
  if (linked && can(ROW_ACTION.OPEN_PATIENT) && !shown(ROW_ACTION.OPEN_PATIENT)) items.push(action(ROW_ACTION.OPEN_PATIENT));
  if (!linked && fallback && can(ROW_ACTION.CREATE_LEAD) && !shown(ROW_ACTION.CREATE_LEAD)) items.push(action(ROW_ACTION.CREATE_LEAD));
  if (!linked && fallback && row?.assignable !== false && can(ROW_ACTION.LINK_EXISTING) && !shown(ROW_ACTION.LINK_EXISTING)) items.push(action(ROW_ACTION.LINK_EXISTING));
  if (can(ROW_ACTION.REVIEW_CALL) && !shown(ROW_ACTION.REVIEW_CALL)) items.push(action(ROW_ACTION.REVIEW_CALL, { label: 'Open call' }));
  return { main, items };
}
